// src/store/slices/currencySlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Currency } from '../../types/currency';
import { getCurrenciesByBranchId } from '../../services/currencyService';

export interface CurrencyState {
    currencies: Currency[];
    selectedCurrencyId: string | null;
    isLoading: boolean;
    error: string | null;
}

const initialState: CurrencyState = {
    currencies: [],
    selectedCurrencyId: null,
    isLoading: false,
    error: null,
};

// Async thunks
export const fetchCurrencies = createAsyncThunk(
    'currency/fetchCurrencies',
    async (branchId: string) => {
        const currencies = await getCurrenciesByBranchId(branchId);

        // Get persisted selection from localStorage
        const storedId = localStorage.getItem(`selectedCurrency:${branchId}`);
        const selectedCurrency = currencies.find((c) => c.id === storedId)
            || currencies.find((c) => !!c.is_base)
            || currencies[0]
            || null;

        return {
            currencies,
            selectedCurrencyId: selectedCurrency?.id || null,
        };
    }
);

const currencySlice = createSlice({
    name: 'currency',
    initialState,
    reducers: {
        setSelectedCurrency: (state, action: PayloadAction<{ branchId: string; currencyId: string }>) => {
            const { branchId, currencyId } = action.payload;
            state.selectedCurrencyId = currencyId;

            // Persist to localStorage
            localStorage.setItem(`selectedCurrency:${branchId}`, currencyId);
        },
        clearCurrencies: (state) => {
            state.currencies = [];
            state.selectedCurrencyId = null;
        },
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCurrencies.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchCurrencies.fulfilled, (state, action) => {
                state.isLoading = false;
                state.currencies = action.payload.currencies;
                state.selectedCurrencyId = action.payload.selectedCurrencyId;
                state.error = null;
            })
            .addCase(fetchCurrencies.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.error.message || 'Failed to fetch currencies';
            });
    },
});

export const { setSelectedCurrency, clearCurrencies, clearError } = currencySlice.actions;
export default currencySlice.reducer;
